import React from 'react';
import PropTypes from 'prop-types';
import { components } from '@glowforge/ui-common';
import _ from 'lodash';
import { Link } from 'react-isomorphic-render';
import withCountryCode, { getValueFromCountryCode } from 'app/common/containers/country-code';
import ProductSelector, { productPropType } from './product-selector';

import './product-list.less';

const { SVG } = components;

const UnitProduct = (id, name, {
  products,
  pageStrings,
  pageMarkdown,
  selectedProduct,
  referralInfo,
  countryCode,
  isIpAddressInUS,
}, backorderable) => {
  if (products[id] == null) {
    return null;
  }

  const greenText = getValueFromCountryCode(`order-page-green-text-string-${id}`, countryCode, pageMarkdown);

  return (
    <ProductSelector
      {...products[id]}
      className={`${id}-selector`}
      badge={pageStrings[`order-${id}-badge`]}
      badgeHidden={pageStrings[`order-${id}-badge[hidden]`]}
      basePrice={pageStrings[`glowforge-${id}-base-price`]}
      selectedProduct={selectedProduct}
      showReferralInfo={referralInfo.showReferralInfo}
      isIpAddressInUS={isIpAddressInUS}
      includeAffirm
      greenText={greenText}
      sellOutable={!backorderable}
      soldOutMessage={pageMarkdown['sold-out-message']}
      displayName={name} />);
};

const FilterProduct = (id, name, { products, selectedProduct }) => {
  if (products[id] == null) {
    return null;
  }

  return (
    <ProductSelector
      type='checkbox'
      {...products[id]}
      className={'air-selector'}
      name={`${id}Filter`}
      displayName={name}
      selectedProduct={selectedProduct} />);
};

/*
  Product List uses Contentful strings from the order page to drive text in each of the product
  selectors.

  The badge has been enabled or disabled by adding '[disabled]' to the id
  (i.e. 'order-plus-badge' changed to 'order-plus-badge[disabled]' to remove it from the page.
  This effectively hides that string from the lookup in pageStrings.
  Also supported is '[hidden]', which renders the badge, but with display:none to keep it from
  showing but to allow A/B test administration. (i.e. 'order-plus-badge[hidden]')
 */
const ProductList = (props) => {
  const {
    features,
    location,
    pageStrings,
    pageMarkdown,
    products,
    countryCode,
    isIpAddressInUS,
  } = props;

  const proName = (
    <span>Glowforge
      <span className='pro'> Pro <SVG className='badge' id='badge-icon-pro' /></span>
    </span>
  );
  const plusName = <span>Glowforge <span className='pro'>Plus</span></span>;

  const backorderable = (_.get(location, 'query.backorderable') ||
    !_.get(features, 'BACKORDERED_SOLD_OUT'));

  const backorderedGlowforges = _.filter(_.reject(products, products.air), 'backordered');
  let backorderingOption;
  if (!_.isEmpty(backorderedGlowforges) && !backorderable) {
    backorderingOption = (
      <Link className='backorderingOption' to='/m/ordering-options'>
        {pageMarkdown['backorder-options-link']}
      </Link>
    );
  }

  // Purposefully not showing Air Filters if not coming from US
  // to prevent users from seeing air filter they can't have
  //
  // TODO: Switch over to Glowforge Air Filter once the data is live
  //
  const filters = (isIpAddressInUS || features.AIR_FILTER_AVAILABLE_EVERYWHERE) ? (
    <React.Fragment>
      <div className='plusAir' />
      {FilterProduct('air', 'Glowforge Air Filter', props)}
    </React.Fragment>
  ) : null;

  return (
    <div className='ProductList'>
      {UnitProduct('pro', proName, props, backorderable)}
      {UnitProduct('plus', plusName, props, backorderable)}
      {UnitProduct('basic', null, props, backorderable)}
      {backorderingOption}
      {filters}
    </div>
  );
};

ProductList.propTypes = {
  products: PropTypes.objectOf(PropTypes.shape(productPropType)).isRequired,
  pageStrings: PropTypes.objectOf(PropTypes.string).isRequired,
  pageMarkdown: PropTypes.objectOf(PropTypes.string).isRequired,
  selectedProduct: PropTypes.string,
  isIpAddressInUS: PropTypes.bool,
  referralInfo: PropTypes.shape({ showReferralInfo: PropTypes.bool }),
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  features: PropTypes.shape({
    BACKORDERED_SOLD_OUT: PropTypes.bool,
    BACKORDERING_OPTION: PropTypes.bool,
    AIR_FILTER_AVAILABLE_EVERYWHERE: PropTypes.bool,
  }),
  countryCode: PropTypes.string,
};

ProductList.defaultProps = {
  selectedProduct: null,
  referralInfo: { showReferralInfo: false },
  features: PropTypes.shape({
    BACKORDERED_SOLD_OUT: false,
    BACKORDERING_OPTION: false,
    AIR_FILTER_AVAILABLE_EVERYWHERE: false,
  }),
  isIpAddressInUS: false,
  countryCode: null,
};

export default withCountryCode(ProductList);



// WEBPACK FOOTER //
// ./app/store/components/glowforge/product-list.jsx